import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import Breadcrumb from '../../../components/Common/Breadcrumb'
import resourceCalendarService from '../../../services/resourceCalendarService'
import { getAllProjects } from '../../../services/projectService'
import employeeMasterService from '../../../services/employeeMasterService'

const EVENT_TYPES = ['Allocation', 'Leave', 'Training', 'Meeting', 'Blocked', 'Other']
const STATUSES = ['Scheduled', 'Confirmed', 'Cancelled']

const employeeLabel = (emp) => {
  if (!emp) return ''
  if (typeof emp === 'object') {
    return emp.employeeName || `${emp.firstName || ''} ${emp.lastName || ''}`.trim()
  }
  return String(emp)
}

const ResourceCalendarForm = ({ mode = 'create' }) => {
  const navigate = useNavigate()
  const { id } = useParams()
  const path = typeof window !== 'undefined' ? window.location.pathname : ''
  const isEditMode = mode === 'edit' || path.includes('/edit/')
  const isViewMode = mode === 'view' || path.includes('/view/')
  const isReadOnly = isViewMode
  const recordId = id

  const [loading, setLoading] = useState(false)
  const [dataLoading, setDataLoading] = useState(false)
  const [projects, setProjects] = useState([])
  const [employees, setEmployees] = useState([])
  const [formData, setFormData] = useState({
    eventNumber: '',
    title: '',
    description: '',
    project: '',
    employee: '',
    eventType: 'Meeting',
    startDate: '',
    endDate: '',
    allDay: true,
    allocationPercent: 100,
    status: 'Scheduled',
    color: '#6A4CA2'
  })
  const [errors, setErrors] = useState({})

  useEffect(() => {
    loadMasterData()
  }, [])

  useEffect(() => {
    if ((isEditMode || isViewMode) && recordId) {
      loadRecord()
    }
  }, [recordId, isEditMode, isViewMode])

  const loadMasterData = async () => {
    try {
      const [projRes, empRes] = await Promise.all([
        getAllProjects({ limit: 1000 }),
        employeeMasterService.getAllEmployees({ limit: 1000 })
      ])
      setProjects(projRes?.projects || projRes?.data || [])
      setEmployees(empRes?.data || empRes?.employees || [])
    } catch (e) {
      console.error(e)
      toast.error('Failed to load master data')
    }
  }

  const loadRecord = async () => {
    try {
      setDataLoading(true)
      const res = await resourceCalendarService.getById(recordId)
      if (res.success && res.data) {
        const e = res.data
        setFormData({
          eventNumber: e.eventNumber || '',
          title: e.title || '',
          description: e.description || '',
          project: e.project?._id || e.project || '',
          employee: e.employee?._id || e.employee || '',
          eventType: e.eventType || 'Meeting',
          startDate: e.startDate ? new Date(e.startDate).toISOString().split('T')[0] : '',
          endDate: e.endDate ? new Date(e.endDate).toISOString().split('T')[0] : '',
          allDay: e.allDay !== false,
          allocationPercent: e.allocationPercent ?? 100,
          status: e.status || 'Scheduled',
          color: e.color || '#6A4CA2'
        })
      } else {
        toast.error('Event not found')
        navigate('/projects/resource-calendar')
      }
    } catch {
      toast.error('Failed to load event')
      navigate('/projects/resource-calendar')
    } finally {
      setDataLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev }
        delete next[name]
        return next
      })
    }
  }

  const validate = () => {
    const next = {}
    if (!formData.title?.trim()) next.title = 'Title is required'
    if (!formData.employee) next.employee = 'Employee is required'
    if (!formData.startDate) next.startDate = 'Start date is required'
    if (!formData.endDate) next.endDate = 'End date is required'
    if (formData.startDate && formData.endDate && formData.endDate < formData.startDate) {
      next.endDate = 'End date must be on or after start date'
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const buildPayload = () => ({
    title: formData.title.trim(),
    description: formData.description?.trim() || '',
    project: formData.project || null,
    employee: formData.employee,
    eventType: formData.eventType,
    startDate: formData.startDate,
    endDate: formData.endDate,
    allDay: formData.allDay,
    allocationPercent: Number(formData.allocationPercent) || 0,
    status: formData.status,
    color: formData.color || '#6A4CA2'
  })

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) {
      toast.error('Please fix validation errors')
      return
    }
    try {
      setLoading(true)
      const payload = buildPayload()
      const res = isEditMode
        ? await resourceCalendarService.update(recordId, payload)
        : await resourceCalendarService.create(payload)
      if (res.success) {
        toast.success(isEditMode ? 'Event updated' : 'Event created')
        navigate('/projects/resource-calendar')
      } else {
        toast.error(res.message || 'Save failed')
      }
    } catch {
      toast.error('Failed to save event')
    } finally {
      setLoading(false)
    }
  }

  const breadcrumbItems = [
    { label: 'Projects', path: '/projects', onClick: () => navigate('/projects') },
    { label: 'Transactions', path: '/projects?tab=transactions', onClick: () => navigate('/projects?tab=transactions') },
    { label: 'Resource Calendar', path: '/projects/resource-calendar', onClick: () => navigate('/projects/resource-calendar') },
    { label: isEditMode ? 'Edit' : isViewMode ? 'View' : 'New', isCurrent: true }
  ]

  if (dataLoading) {
    return (
      <div className="sales-dashboard">
        <div className="loading-container">
          <div className="loading-spinner" />
          <p>Loading event...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="sales-dashboard">
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>
      <div className="page-header">
        <div className="page-title">
          <h1>{isEditMode ? 'Edit Calendar Event' : isViewMode ? 'View Calendar Event' : 'New Calendar Event'}</h1>
          <p>Schedule resource availability, leave, and project events</p>
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header"><h2>Event Details</h2></div>
        <form className="form-content" onSubmit={handleSubmit}>
          <div className="section-header">
            <h3>Event Information</h3>
          </div>
          <div className="grid-row-4">
            <div className="form-group">
              <label className="form-label">Event #</label>
              <input readOnly className="form-input" value={formData.eventNumber || 'Auto-generated on save'} style={{ backgroundColor: '#f9fafb' }} />
            </div>
            <div className="form-group">
              <label className="form-label required">Title</label>
              <input type="text" name="title" value={formData.title} onChange={handleChange} className="form-input" disabled={isReadOnly} />
              {errors.title && <p className="form-error">{errors.title}</p>}
            </div>
            <div className="form-group">
              <label className="form-label required">Employee</label>
              <select name="employee" value={formData.employee} onChange={handleChange} className="form-input" disabled={isReadOnly}>
                <option value="">Select employee</option>
                {employees.map((emp) => (
                  <option key={emp._id || emp.id} value={emp._id || emp.id}>
                    {(emp.employeeCode || emp.code) ? `${emp.employeeCode || emp.code} — ` : ''}
                    {employeeLabel(emp)}
                  </option>
                ))}
              </select>
              {errors.employee && <p className="form-error">{errors.employee}</p>}
            </div>
            <div className="form-group">
              <label className="form-label">Project</label>
              <select name="project" value={formData.project} onChange={handleChange} className="form-input" disabled={isReadOnly}>
                <option value="">Optional</option>
                {projects.map((p) => (
                  <option key={p._id || p.id} value={p._id || p.id}>
                    {p.projectNumber} — {p.projectName}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-section-divider">
            <div className="section-header">
              <h3>Schedule &amp; Allocation</h3>
            </div>
          </div>

          <div className="grid-row-4">
            <div className="form-group">
              <label className="form-label">Event Type</label>
              <select name="eventType" value={formData.eventType} onChange={handleChange} className="form-input" disabled={isReadOnly}>
                {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label required">Start Date</label>
              <input type="date" name="startDate" value={formData.startDate} onChange={handleChange} className="form-input" disabled={isReadOnly} />
              {errors.startDate && <p className="form-error">{errors.startDate}</p>}
            </div>
            <div className="form-group">
              <label className="form-label required">End Date</label>
              <input type="date" name="endDate" value={formData.endDate} onChange={handleChange} className="form-input" disabled={isReadOnly} />
              {errors.endDate && <p className="form-error">{errors.endDate}</p>}
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select name="status" value={formData.status} onChange={handleChange} className="form-input" disabled={isReadOnly}>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <div className="grid-row-4">
            <div className="form-group">
              <label className="form-label">Allocation %</label>
              <input type="number" name="allocationPercent" min={0} max={100} value={formData.allocationPercent} onChange={handleChange} className="form-input" disabled={isReadOnly} />
            </div>
            <div className="form-group">
              <label className="form-label">Color</label>
              <input type="color" name="color" value={formData.color} onChange={handleChange} className="form-input" disabled={isReadOnly} style={{ height: '42px', padding: '4px' }} />
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: isReadOnly ? 'default' : 'pointer' }}>
                <input type="checkbox" name="allDay" checked={formData.allDay} onChange={handleChange} disabled={isReadOnly} />
                All day event
              </label>
            </div>
          </div>

          <div className="form-section-divider">
            <div className="section-header">
              <h3>Description</h3>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea name="description" value={formData.description} onChange={handleChange} className="form-input" rows={4} disabled={isReadOnly} />
          </div>

          {!isViewMode && (
            <div className="form-actions">
              <div style={{ display: 'flex', gap: '12px' }}>
                <button type="button" className="btn-secondary" onClick={() => navigate('/projects/resource-calendar')}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : isEditMode ? 'Update Event' : 'Create Event'}
                </button>
              </div>
            </div>
          )}
          {isViewMode && (
            <div className="form-actions" style={{ justifyContent: 'flex-end', gap: '12px' }}>
              <button type="button" className="btn-secondary" onClick={() => navigate('/projects/resource-calendar')}>Back</button>
              <button type="button" className="btn-primary" onClick={() => navigate(`/projects/resource-calendar/edit/${recordId}`)}>Edit</button>
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

export default ResourceCalendarForm
